
const DEFAULT_DEBOUNCE_MS = 300;

function defaultOpenChat(chatId) {
  if (!chatId || typeof document === 'undefined') {
    return;
  }
  try {
    document.dispatchEvent(new CustomEvent('chat:open', { detail: { id: chatId } }));
  } catch (error) {
    // ignore dispatch errors
  }
}

export class SearchController {
  constructor({ view, presenter, debounceMs = DEFAULT_DEBOUNCE_MS, openChat } = {}) {
    if (!view || typeof view.mount !== 'function') {
      throw new Error('SearchController requires a view with mount');
    }
    if (!presenter || typeof presenter.search !== 'function') {
      throw new Error('SearchController requires a presenter with search');
    }
    this.view = view;
    this.presenter = presenter;
    this.debounceMs = typeof debounceMs === 'number' && debounceMs >= 0 ? debounceMs : DEFAULT_DEBOUNCE_MS;
    this.openChat = typeof openChat === 'function' ? openChat : defaultOpenChat;
    this.searchTimer = null;
    this.activeRequestId = 0;
    this.lastResolvedRequestId = 0;
  }

  init() {
    this.view.mount({
      onOpen: () => this.handleOpen(),
      onQueryChange: (value) => this.handleQueryChange(value),
      onSubmit: (value) => this.handleSubmit(value),
      onResultSelect: (chatId) => this.handleResultSelect(chatId),
    });
  }

  teardown() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    if (typeof this.view.destroy === 'function') {
      this.view.destroy();
    }
  }

  handleOpen() {
    this.view.openModal();
  }

  handleQueryChange(value) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    if (!trimmed) {
      this.view.showState('empty');
      return;
    }
    if (this.debounceMs === 0) {
      this.#runSearch(trimmed);
      return;
    }
    this.searchTimer = setTimeout(() => {
      this.#runSearch(trimmed);
    }, this.debounceMs);
  }

  handleSubmit(value) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    if (!trimmed) {
      this.view.showState('empty');
      return;
    }
    this.#runSearch(trimmed);
  }

  async handleResultSelect(chatId) {
    if (!chatId) {
      return;
    }
    this.openChat(chatId);
  }

  async search(query) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!trimmed) {
      this.view.showState('empty');
      return { results: [], source: 'empty' };
    }
    return this.#runSearch(trimmed);
  }

  async #runSearch(query) {
    const requestId = ++this.activeRequestId;
    this.view.showState('loading');
    try {
      const payload = await this.presenter.search(query);
      if (requestId < this.lastResolvedRequestId) {
        return payload;
      }
      this.lastResolvedRequestId = requestId;
      const results = Array.isArray(payload?.results) ? payload.results : [];
      if (results.length) {
        this.view.renderResults(results);
        this.view.showState('results');
      } else {
        this.view.renderResults([]);
        this.view.showState('no-results');
      }
      return payload;
    } catch (error) {
      if (requestId > this.lastResolvedRequestId) {
        this.lastResolvedRequestId = requestId;
        this.view.renderResults([]);
        this.view.showState('no-results');
      }
      throw error;
    }
  }
}

if (typeof window !== 'undefined') {
  window.AgileProControllers = window.AgileProControllers || {};
  window.AgileProControllers.SearchController = SearchController;
}
